import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, ArrowRight } from "lucide-react";
import { formatPrice } from "../../lib/filters";
import { useWishlist } from "../../context/WishlistContext";
import { toast } from "../ui/sonner";
import { AutoAspectImage } from "../ui/AutoAspectImage";
import { CardImageCarousel } from "./CardImageCarousel";

const BADGE_STYLES = {
  Bestseller: "bg-charcoal text-ivory",
  New: "bg-taupe text-ivory",
  Compact: "bg-ivory text-charcoal border border-beige",
  "Premium Finish": "bg-ivory text-charcoal border border-beige",
};

export const ProductCard = ({ product, index = 0, autoplay = false }) => {
  const { has, toggle } = useWishlist();
  const wished = has(product.id);
  const toggleWish = () => {
    toggle({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      mounting: product.mounting,
      style: product.style,
      width: product.width,
      finish: product.finish,
      sink: product.sink,
    });
    toast(wished ? "Removed from wishlist" : "Saved to wishlist", { description: product.name });
  };
  const meta = [
    product.mounting,
    product.width != null ? `${product.width}" Width` : null,
    product.sink,
    product.style,
  ]
    .filter(Boolean)
    .slice(0, 4);

  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: Math.min(index, 6) * 0.04, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`product-card-${product.id}`}
      className="group flex flex-col"
    >
      <div className="relative overflow-hidden rounded-sm bg-stone spotlight">
        <Link to={`/vanities/${product.id}`} className="block overflow-hidden" data-testid={`product-image-${product.id}`}>
          {autoplay && (product.images ?? []).length > 1 ? (
            <CardImageCarousel images={product.images} name={product.name} />
          ) : (
            <AutoAspectImage
              src={product.image}
              alt={product.name}
              label={product.name}
              loading="lazy"
              className="transition-transform duration-[1100ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105"
            />
          )}
        </Link>

        {/* Badges */}
        {product.badges.length > 0 && (
          <div className="absolute top-3 left-3 flex flex-col items-start gap-1.5">
            {product.badges.slice(0, 2).map((b) => (
              <span
                key={b}
                data-testid={`badge-${product.id}-${b.toLowerCase().replace(/\s+/g, "-")}`}
                className={`text-[0.62rem] font-medium uppercase tracking-[0.14em] px-2.5 py-1 rounded-full ${BADGE_STYLES[b] || "bg-ivory text-charcoal"}`}
              >
                {b}
              </span>
            ))}
          </div>
        )}

        {/* Wishlist */}
        <motion.button
          whileTap={{ scale: 0.85 }}
          onClick={toggleWish}
          data-testid={`wishlist-${product.id}`}
          aria-label="Add to wishlist"
          aria-pressed={wished}
          className="absolute top-3 right-3 grid place-items-center h-9 w-9 rounded-full bg-ivory/90 backdrop-blur-sm text-charcoal transition-transform duration-300 hover:scale-110"
        >
          <Heart
            size={16}
            strokeWidth={1.6}
            className={wished ? "fill-charcoal" : ""}
          />
        </motion.button>
      </div>

      <div className="pt-4 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-serif text-xl md:text-[1.4rem] text-charcoal leading-tight">
            <Link to={`/vanities/${product.id}`} className="hover:text-taupe transition-colors">
              {product.name}
            </Link>
          </h3>
          <p className="font-serif text-lg md:text-xl text-charcoal whitespace-nowrap">
            {formatPrice(product.price)}
          </p>
        </div>

        {/* Metadata chips (max 4) */}
        <div className="mt-3 flex flex-wrap gap-1.5">
          {meta.map((m) => (
            <span
              key={m}
              className="text-[0.68rem] text-charcoal/70 bg-stone/70 border border-beige/70 px-2 py-1 rounded-full"
            >
              {m}
            </span>
          ))}
        </div>

        <Link
          to={`/vanities/${product.id}`}
          data-testid={`product-view-${product.id}`}
          className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-charcoal link-sweep w-fit"
        >
          View Details
          <ArrowRight
            size={15}
            strokeWidth={1.5}
            className="transition-transform duration-500 group-hover:translate-x-1"
          />
        </Link>
      </div>
    </motion.article>
  );
};
